/**
 * CreateScenarioDialog Component
 * Modal dialog for creating and editing evaluation scenarios
 */

import React, { useEffect } from 'react';
import { X, Plus, Edit2 } from 'lucide-react';
import { ScenarioForm } from './ScenarioForm';
import { useI18n } from '@/i18n';
import type {
  EvalScenario,
  CreateEvalScenarioRequest,
  UpdateEvalScenarioRequest,
  AgentTypeOption,
  JudgerModelOption,
} from '@/types/evalScenarios';

interface CreateScenarioDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateEvalScenarioRequest | UpdateEvalScenarioRequest) => void;
  scenario?: EvalScenario | null;
  isSubmitting?: boolean;
  categories: string[];
  agentTypes: AgentTypeOption[];
  judgerModels: JudgerModelOption[];
}

export const CreateScenarioDialog: React.FC<CreateScenarioDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  scenario,
  isSubmitting = false,
  categories,
  agentTypes,
  judgerModels,
}) => {
  const { messages } = useI18n();
  const t = messages.evalScenarios;

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isEditing = !!scenario;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop - no onClick to prevent accidental close */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Dialog - larger size for better usability */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <Edit2 className="w-5 h-5 text-gray-500" />
            ) : (
              <Plus className="w-5 h-5 text-gray-500" />
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? t.editDialogTitle : t.createDialogTitle}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <div className="p-4 overflow-y-auto flex-1">
          <ScenarioForm
            scenario={scenario}
            onSubmit={onSubmit}
            onCancel={onClose}
            isSubmitting={isSubmitting}
            categories={categories}
            agentTypes={agentTypes}
            judgerModels={judgerModels}
          />
        </div>
      </div>
    </div>
  );
};
